// =============================================================================
// features/admin/document-types/hooks.ts — TanStack Query hooks do catálogo
// de tipos de documento (F46-S12).
//
// Hooks:
//   - useDocumentTypes()        — lista o catálogo (query)
//   - useCreateDocumentType()   — cria um tipo (mutation)
//   - useUpdateDocumentType()   — edita um tipo, inclusive `active` (mutation)
//
// Nunca useEffect + fetch — apenas TanStack Query. Toda mutation invalida a
// query da lista para o cache não ficar velho (ex.: alternar `active` some
// do seletor de upload em F46-S19 assim que reconsultado).
// =============================================================================

import type {
  DocumentType,
  DocumentTypeCreate,
  DocumentTypeUpdate,
} from '@elemento/shared-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useToast } from '../../../components/ui/Toast';
import { ApiError } from '../../../lib/api';

import { createDocumentType, listDocumentTypes, updateDocumentType } from './api';

export const DOCUMENT_TYPES_QUERY_KEY = ['admin', 'document-types'] as const;

// ---------------------------------------------------------------------------
// useDocumentTypes
// ---------------------------------------------------------------------------

export function useDocumentTypes(enabled = true): {
  data: DocumentType[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: DOCUMENT_TYPES_QUERY_KEY,
    queryFn: listDocumentTypes,
    enabled,
    staleTime: 15_000,
  });

  return { data, isLoading, isError, refetch };
}

// ---------------------------------------------------------------------------
// useCreateDocumentType
// ---------------------------------------------------------------------------

interface UseCreateDocumentTypeOptions {
  onSuccess?: (documentType: DocumentType) => void;
  /** Conflito 409 (key duplicada na organização) — mensagem para exibição inline. */
  onConflict?: (message: string) => void;
}

export function useCreateDocumentType(opts: UseCreateDocumentTypeOptions = {}): {
  mutate: (body: DocumentTypeCreate) => void;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: (body: DocumentTypeCreate) => createDocumentType(body),
    onSuccess: (documentType) => {
      void queryClient.invalidateQueries({ queryKey: DOCUMENT_TYPES_QUERY_KEY });
      toast('Tipo de documento criado com sucesso.', 'success');
      opts.onSuccess?.(documentType);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        opts.onConflict?.(err.message);
        return;
      }
      const message = err instanceof Error ? err.message : 'Erro ao criar tipo de documento.';
      toast(message, 'danger');
    },
  });

  return { mutate: mutation.mutate, isPending: mutation.isPending };
}

// ---------------------------------------------------------------------------
// useUpdateDocumentType
// ---------------------------------------------------------------------------

interface UseUpdateDocumentTypeOptions {
  onSuccess?: (documentType: DocumentType) => void;
  onConflict?: (message: string) => void;
}

export function useUpdateDocumentType(opts: UseUpdateDocumentTypeOptions = {}): {
  mutate: (args: { id: string; body: DocumentTypeUpdate }) => void;
  isPending: boolean;
  pendingId: string | null;
} {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: DocumentTypeUpdate }) =>
      updateDocumentType(id, body),
    onSuccess: (documentType) => {
      void queryClient.invalidateQueries({ queryKey: DOCUMENT_TYPES_QUERY_KEY });
      toast('Tipo de documento atualizado.', 'success');
      opts.onSuccess?.(documentType);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        opts.onConflict?.(err.message);
        return;
      }
      const message = err instanceof Error ? err.message : 'Erro ao atualizar tipo de documento.';
      toast(message, 'danger');
    },
  });

  return {
    mutate: mutation.mutate,
    isPending: mutation.isPending,
    pendingId: mutation.isPending ? (mutation.variables?.id ?? null) : null,
  };
}
